'use client';

import { useTranslations } from 'next-intl';
import { Image as ImageIcon, Check } from 'lucide-react';
import Image from 'next/image';
import { useSubscriptionLimits } from '@/features/subscription';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import { useOutputStyles } from '@/shared/api/hooks/use-output-styles';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { cn } from '@/shared/lib/cn';
import { ProjectReferenceSelector } from '../scrollable-settings/ProjectReferenceSelector';

export interface OutputContentProps {
  selectedOutputStyleId: number | null;
  onOutputStyleChange: (styleId: number | null) => void;
  // Layout variant — kept for API parity; both variants share the Apple
  // single-column preset-card idiom now.
  variant?: 'default' | 'compact';
  closeOnSelect?: () => void;
  // Project reference props (optional)
  projectId?: string;
  outputProjectReferenceIds?: string[] | null;
  onOutputProjectReferencesChange?: (ids: string[] | null) => void;
  onNavigateToReferences?: () => void;
}

export function OutputContent({
  selectedOutputStyleId,
  onOutputStyleChange,
  closeOnSelect,
  projectId,
  outputProjectReferenceIds,
  onOutputProjectReferencesChange,
  onNavigateToReferences,
}: OutputContentProps) {
  const t = useTranslations('render.settings.output');
  const limits = useSubscriptionLimits();
  const hasOutputStyle = limits?.has_output_style ?? false;
  const { data: outputStyles = [], isLoading } = useOutputStyles();

  // Output style is Designer-exclusive (EntitlementFeatureMap: output_style).
  // Any locked interaction here upsells the Designer Pass (paywall opens with
  // the Designer disclosure pre-expanded) — never the deprecated subscription
  // paywall.
  const openPassPaywall = usePassPaywallStore((s) => s.open);

  const hasSelectedRefs = (outputProjectReferenceIds?.length ?? 0) > 0;
  // Photorealistic is selected when no style AND no refs
  const isPhotorealistic = selectedOutputStyleId === null && !hasSelectedRefs;
  // From reference is selected when refs are chosen
  const isFromReference = hasSelectedRefs;

  const triggerPaywall = () => {
    openPassPaywall('designer');
  };

  const handleSelect = (styleId: number | null) => {
    // Photorealistic (null) is the free baseline — always selectable, even when
    // locked. Only the Designer-gated artistic presets route to the paywall.
    if (styleId !== null && !hasOutputStyle) {
      triggerPaywall();
      return;
    }
    if (hasSelectedRefs) {
      onOutputProjectReferencesChange?.(null);
    }
    onOutputStyleChange(styleId);
    closeOnSelect?.();
  };

  const handleRefsSelectionChange = (ids: string[]) => {
    if (!hasOutputStyle) {
      triggerPaywall();
      return;
    }
    if (ids.length > 0) {
      onOutputStyleChange(null);
    }
    onOutputProjectReferencesChange?.(ids.length > 0 ? ids : null);
  };

  return (
    <div className="space-y-4">
      {/* Photorealistic — Apple parity: bordered pill row (xl corner radius,
          accent stroke when active). */}
      <button
        type="button"
        className={cn(
          'w-full flex items-center gap-3 py-3 px-4 rounded-2xl text-left',
          'transition-colors',
          isPhotorealistic
            ? 'border-2 border-primary'
            : 'border border-border/60'
        )}
        onClick={() => handleSelect(null)}
      >
        <div className="w-11 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
          <ImageIcon
            className={cn(
              'w-4 h-4',
              isPhotorealistic ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        </div>
        <span className="flex-1 text-card-body text-foreground">
          {t('photorealistic')}
        </span>
        {isPhotorealistic && (
          <Check
            className="w-5 h-5 text-primary flex-shrink-0"
            strokeWidth={2.5}
          />
        )}
      </button>

      {/* Selected references / Custom style. When locked, the reference picker
          is replaced by a plain "Custom style" row carrying the Designer badge —
          tapping it opens the paywall. Mirrors Apple's OutputStylePickerView. */}
      {projectId &&
        onOutputProjectReferencesChange &&
        (!hasOutputStyle ? (
          <button
            type="button"
            className={cn(
              'w-full flex items-center gap-3 py-3 px-4 rounded-2xl text-left',
              'transition-colors border border-border/60'
            )}
            onClick={triggerPaywall}
          >
            <span className="flex-1 text-card-body text-foreground">
              {t('customStyle')}
            </span>
            <ProBadge tier="designer" isLocked />
          </button>
        ) : (
          <ProjectReferenceSelector
            projectId={projectId}
            category="output"
            selectedIds={outputProjectReferenceIds || []}
            onSelectionChange={handleRefsSelectionChange}
            isSelected={isFromReference}
            maxSelection={1}
            onNavigateToReferences={onNavigateToReferences}
          />
        ))}

      {/* Artistic style preset cards — full-bleed preview + name + checkmark.
          Mirrors Apple `OutputStylePresetCard`: small leading thumbnails made
          every preset look identical on phones; full-bleed previews let users
          actually distinguish styles. */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden border border-border/60"
            >
              <Skeleton className="w-full aspect-[168/120] rounded-none" />
              <div className="p-3">
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        outputStyles.length > 0 && (
          <div className="space-y-3">
            <p className="text-form-label text-muted-foreground px-1">
              {t('artistic')}
            </p>
            {outputStyles.map((style) => {
              const isSelected =
                hasOutputStyle &&
                selectedOutputStyleId === parseInt(style.id);

              return (
                <button
                  type="button"
                  key={style.id}
                  className={cn(
                    'block w-full text-left rounded-xl overflow-hidden transition-colors',
                    'bg-muted/40',
                    isSelected
                      ? 'border-2 border-primary'
                      : 'border border-border/60'
                  )}
                  onClick={() => {
                    if (!hasOutputStyle) {
                      triggerPaywall();
                      return;
                    }
                    handleSelect(isSelected ? null : parseInt(style.id));
                  }}
                >
                  <div className="relative w-full aspect-[168/120] bg-muted overflow-hidden">
                    <Image
                      src={style.attributes.preview_image_url}
                      alt={style.attributes.name}
                      fill
                      sizes="(max-width: 480px) 100vw, 480px"
                      className="object-cover"
                    />
                  </div>
                  <div className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        'text-card-body',
                        isSelected
                          ? 'text-primary font-semibold'
                          : 'text-foreground'
                      )}
                    >
                      {style.attributes.name}
                    </span>
                    {/* Locked presets carry the Designer badge; the card stays
                        full-color and tapping it opens the paywall. */}
                    {!hasOutputStyle ? (
                      <ProBadge tier="designer" isLocked />
                    ) : (
                      isSelected && (
                        <Check
                          className="w-5 h-5 text-primary flex-shrink-0"
                          strokeWidth={2.5}
                        />
                      )
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// Helper to get subtitle for preview
export function getOutputSubtitle(
  selectedOutputStyleId: number | null,
  outputStyles: { id: string; attributes: { name: string } }[],
  t: (key: string) => string
): string {
  if (selectedOutputStyleId === null) {
    return t('photorealistic');
  }
  const selectedStyle = outputStyles.find(
    (s) => parseInt(s.id) === selectedOutputStyleId
  );
  return selectedStyle?.attributes.name || t('photorealistic');
}
